import i18n from '@/utils/i18n';
import React, { useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import {
  AlertKeys,
  useUserContext,
  useUserSettingsContext,
} from '@/contexts/user-context';
import { ApplicationType } from '@/contexts/dynamic-config';
import { BadgeLabelList } from './badge-label-list';
import {
  BORDER,
  LIST_MARGIN_HORIZONTAL,
  SCREEN_PADDING,
  TYPOGRAPHY,
} from '@/styles';
import { BrandPageLink } from '@/components/brand-page/brand-page-link';
import { Button } from '@/components/button';
import { getParamsFromNameValue } from '@/utils/salt-item';
import { getVisibleQuantity } from '@/screens/new-order/hooks/use-quantity-reducer';
import { GTINRichInfo } from './gtin-rich-info';
import { ItemDetailActions } from './item-detail-actions';
import { ItemDetailMetadataLink } from './item-detail-metadata-link';
import { ItemDetailSelector } from './item-detail-selector';
import {
  ItemDetailsImageList,
  LARGE_IMAGE_SIZE,
} from './item-details-image-list';
import { ItemDetailsRecommendedItemList } from './item-details-recommended-item-list';
import { ItemDetailTitleLarge } from './item-detail-title-large';
import { ItemLegalDisclaimer } from './item-legal-disclaimer';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { MultiOrderGuideButton } from './multi-order-guide-button';
import { OrderableItemLabels } from './orderable-item-labels';
import { OrderGuideInfo } from './order-guide-info';
import { OrderGuidePartitionSectionActionSheet } from '@/components/order-guide/order-guide-partition-section-sheet';
import { OrderHistoryHorizontalList } from './order-history-horizontal-list';
import { Props } from '@/utils/item-details';
import { RichInfoSection } from './rich-info-section';
import { SalesRepInfo } from './sales-rep-info';
import { SaltItemRichInfo } from './salt-item-rich-info';
import { SampleRequest } from '@/components/ads/sample-request';
import { TooltipContainer } from '@/components/tooltip-container';
import { TooltipPosition } from '@/enums';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useGetItemDetailsDemoVariantPacks } from '@/hooks/use-get-item-details-demo-variant-packs';
import {
  useItemDisplayOptions,
  useNewOrderContext,
} from '@/contexts/new-order-context';
import { useItemQuantityState } from '@/stores/new-order-quantity-store';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSalesRepMode } from '@/hooks/use-sales-rep-mode';
import { useScrollViewTabsContext } from '@/contexts/scroll-view-tabs-context';

export const ItemDetailsLarge: React.FC<Props> = ({
  availableQuantityMap,
  brandPageDetails,
  gtinMetadata,
  inDraggableSheet = false,
  isSkuInCartOrOrderGuide,
  itemInformation,
  ItemMenuButton,
  onChangeSelector,
  onClose,
  onSelectItem,
  orderHistoryItems,
  renderActions,
  orderHistoryLoading,
  saltItemInfo,
  selectedItem,
  setDSRToolingActionSheetItemMetadata,
  showSelector,
  source,
  updateQuantity,
  variantPackMetadata,
}) => {
  const itemSkuUUIDs = useMemo(
    () => selectedItem.metadata?.skus?.map((sku) => sku.variantPackUUID) ?? [],
    [selectedItem.metadata?.skus],
  );
  const skuQuantityState = useItemQuantityState(itemSkuUUIDs);
  const { bottom } = useSafeAreaInsets();
  const { customOrderGuideEditingEnabled } = useNewOrderContext();
  const { itemModifiersEnabled } = useItemDisplayOptions();
  const { isInOrderGuide } = useOrderGuideItemsContext();
  const { alertsSeen, setAlertSeen } = useUserSettingsContext();
  const salesRepMode = useSalesRepMode();
  const { onScroll: tabsContextOnScroll, scrollViewRef } =
    useScrollViewTabsContext();
  const { isGuestMode } = useUserContext();

  const bottomRowPosition = useRef<number | null>(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [
    partitionSelectorVariantPackUUID,
    setPartitionSelectorVariantPackUUID,
  ] = useState<string | null>(null);
  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );
  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;

  const additionalInfo = variantPackMetadata?.additional_info;
  const { recommendedVariantPacks, relatedVariantPacks } =
    useGetItemDetailsDemoVariantPacks({
      recommendedVariantPackUUIDs:
        variantPackMetadata?.demo_recommended_variant_pack_ids ??
        additionalInfo?.demo_recommended_variant_pack_ids ??
        [],
      relatedVariantPackUUIDs:
        variantPackMetadata?.demo_related_variant_pack_ids ??
        additionalInfo?.demo_related_variant_pack_ids ??
        [],
    });

  const itemSelectorEnabled = useMemo<boolean>(
    () =>
      itemModifiersEnabled &&
      selectedItem!.metadata!.modifierGroups!.length > 0,
    [itemModifiersEnabled, selectedItem],
  );

  const skusToShow = useMemo<ItemListOptionMetadata[]>(() => {
    if (!selectedItem.metadata?.skus) {
      return [];
    }

    return itemSelectorEnabled
      ? // Only show variant packs in order guide or in cart
        selectedItem.metadata.skus.filter(isSkuInCartOrOrderGuide)
      : selectedItem.metadata.skus;
  }, [itemSelectorEnabled, selectedItem, isSkuInCartOrOrderGuide]);

  const tooltipEnabledItemUUID = useMemo<string | null>(() => {
    if (!customOrderGuideEditingEnabled || !selectedItem.metadata) {
      return null;
    }

    const catalogMetadata = (selectedItem.metadata.skus || []).find(
      (skuMetadata) => !isInOrderGuide(skuMetadata.variantPackUUID),
    );

    return catalogMetadata?.itemUUID || null;
  }, [customOrderGuideEditingEnabled, isInOrderGuide, selectedItem]);

  if (!selectedItem.metadata) {
    return null;
  }

  // Show if order guide is editable or at least one variant pack is already
  // in the order guide
  const showOrderGuideButton =
    customOrderGuideEditingEnabled ||
    (!isCatalogLimited &&
      Boolean(
        selectedItem.metadata?.skus.some((sku) =>
          isInOrderGuide(sku.variantPackUUID),
        ),
      ));

  const photoURLs = selectedItem.metadata.photoURLs;

  const itemRichInfoComponent = saltApiIntegrationEnabled ? (
    saltItemInfo ? (
      <SaltItemRichInfo horizontal saltItemInfo={saltItemInfo} />
    ) : null
  ) : gtinMetadata ? (
    <GTINRichInfo gtinMetadata={gtinMetadata} horizontal />
  ) : null;
  const brandName = saltApiIntegrationEnabled
    ? saltItemInfo?.brandInformation.brandName
    : gtinMetadata?.metadata?.brand_name;
  const saltAdditionalInformation =
    saltApiIntegrationEnabled && saltItemInfo
      ? getParamsFromNameValue(
          saltItemInfo.basicInformation.additionalInformation,
        )
      : null;
  const sellSheetURL = saltApiIntegrationEnabled
    ? (saltAdditionalInformation?.sell_sheet_pdf_url ?? null)
    : gtinMetadata?.metadata?.sell_sheet_pdf_url;
  const safetyDataSheetURL =
    saltAdditionalInformation?.safety_data_sheet_pdf_url ?? null;
  const altText = [
    selectedItem.metadata.displayName,
    selectedItem.metadata.externalID,
  ]
    .filter(Boolean)
    .join('-');

  return (
    <>
      <ItemDetailTitleLarge
        hasRichInfo={Boolean(itemRichInfoComponent)}
        isVisible={isHeaderVisible}
        ItemMenuButton={ItemMenuButton}
        onClose={onClose}
        onTitlePress={() => {
          scrollViewRef.current?.scrollTo({
            animated: true,
            y: 0,
          });
        }}
        title={selectedItem.title}
      />
      <ScrollView
        alwaysBounceVertical={false}
        contentContainerStyle={styles.scrollViewContainer}
        keyboardShouldPersistTaps="handled"
        onScroll={
          itemRichInfoComponent
            ? (e) => {
                setIsHeaderVisible(
                  e.nativeEvent.contentOffset.y > LARGE_IMAGE_SIZE,
                );
                tabsContextOnScroll?.(e);
              }
            : undefined
        }
        ref={scrollViewRef}
        scrollEventThrottle={16}
        testID="item-details-scroll-view"
      >
        <View
          style={[
            styles.base,
            {
              paddingBottom: Math.max(bottom, SCREEN_PADDING),
            },
          ]}
        >
          <View style={styles.heading}>
            {photoURLs.length > 0 ? (
              <View style={styles.headingSection}>
                <ItemDetailsImageList
                  alt={altText}
                  imageURLs={photoURLs}
                  size={LARGE_IMAGE_SIZE}
                />
              </View>
            ) : null}
            <View style={styles.headingSection}>
              <OrderableItemLabels option={selectedItem} />
              {brandPageDetails ? (
                <BrandPageLink
                  brandPageDetails={brandPageDetails}
                  onNavigate={onClose}
                  option={selectedItem}
                  source={source}
                />
              ) : brandName ? (
                <Text style={[TYPOGRAPHY.small, styles.brandName]}>
                  {brandName}
                </Text>
              ) : null}
              <Text style={TYPOGRAPHY.h2}>{selectedItem.title}</Text>
              <BadgeLabelList badges={selectedItem.metadata.badges} />
              {itemSelectorEnabled && showSelector ? (
                <ItemDetailSelector
                  closeSelector={onChangeSelector}
                  metadata={selectedItem.metadata}
                  onNavigateToBecomeCustomer={onClose}
                  scrollTo={(y) => {
                    scrollViewRef.current?.scrollTo({ animated: true, y });
                  }}
                  updateQuantity={updateQuantity}
                />
              ) : (
                <View
                  onLayout={(e) => {
                    bottomRowPosition.current = e.nativeEvent.layout.y;
                  }}
                  style={styles.headerActions}
                >
                  {skusToShow.map((sku) => {
                    const renderOrderGuideButton = (
                      variantPackUUID: string,
                    ) => (
                      <TooltipContainer
                        content={i18n.t('add_to_order_guide')}
                        onClose={() => {
                          setAlertSeen(AlertKeys.OrderGuideButton);
                        }}
                        position={TooltipPosition.Left}
                        visible={
                          sku.itemUUID === tooltipEnabledItemUUID &&
                          !alertsSeen[AlertKeys.OrderGuideButton]
                        }
                      >
                        <View
                          // collapsable prevents android from removing the native view and breaking animations
                          collapsable={false}
                        >
                          <MultiOrderGuideButton
                            openPartitionSelector={() => {
                              setPartitionSelectorVariantPackUUID(
                                variantPackUUID,
                              );
                            }}
                            variantPackUUID={variantPackUUID}
                          />
                        </View>
                      </TooltipContainer>
                    );

                    return (
                      <View key={sku.variantPackUUID}>
                        <OrderGuideInfo
                          isLastOrderedMessageVisible={false}
                          isParLevelsVisible
                          orderGuidePartitionUUID={sku.orderGuidePartitionUUID}
                          variantPackUUID={sku.variantPackUUID}
                        />
                        <View style={styles.itemContainer}>
                          <ItemDetailActions
                            inDraggableSheet={inDraggableSheet}
                            isFocused={false}
                            item={sku}
                            onNavigateToBecomeCustomer={onClose}
                            quantity={getVisibleQuantity(
                              skuQuantityState,
                              sku.variantPackUUID,
                            )}
                            quantityInStock={
                              availableQuantityMap?.[sku.variantPackUUID]
                            }
                            renderActions={renderActions}
                            renderOrderGuideButton={
                              showOrderGuideButton
                                ? renderOrderGuideButton
                                : undefined
                            }
                            updateQuantity={updateQuantity}
                          />
                          {salesRepMode ? (
                            <SalesRepInfo
                              inDraggableSheet={inDraggableSheet}
                              onPress={() => {
                                setDSRToolingActionSheetItemMetadata(sku);
                              }}
                              variantPackUUID={sku.variantPackUUID}
                            />
                          ) : null}
                        </View>
                      </View>
                    );
                  })}
                  {itemSelectorEnabled ? (
                    <Button
                      onPress={onChangeSelector}
                      rounded
                      testID="more-options-button"
                      trackingData={{
                        name: 'item_detail.more_options_button',
                        properties: {
                          itemUUID: selectedItem.metadata.itemUUID,
                        },
                      }}
                      type="secondary"
                    >
                      {i18n.t('more_options')}
                    </Button>
                  ) : null}
                  {!isGuestMode ? (
                    <SampleRequest
                      option={selectedItem}
                      source={source}
                      style={styles.sampleRequestContainer}
                    />
                  ) : null}
                  {orderHistoryLoading ? (
                    <ActivityIndicator />
                  ) : orderHistoryItems.length ? (
                    <OrderHistoryHorizontalList
                      orderHistoryItems={orderHistoryItems}
                    />
                  ) : null}
                </View>
              )}
            </View>
          </View>
          {selectedItem.metadata.description ? (
            <RichInfoSection horizontal title={i18n.t('description')}>
              <Text style={TYPOGRAPHY.small}>
                {selectedItem.metadata.description}
              </Text>
            </RichInfoSection>
          ) : null}
          {recommendedVariantPacks.length ? (
            <RichInfoSection horizontal title={i18n.t('you_might_also_like')}>
              <ItemDetailsRecommendedItemList
                items={recommendedVariantPacks}
                onSelect={(item) => {
                  onSelectItem(item, 'item_detail_recommended_items');
                }}
                updateQuantity={updateQuantity}
              />
            </RichInfoSection>
          ) : null}
          {itemRichInfoComponent}
          <ItemDetailMetadataLink
            itemInformation={itemInformation}
            safetyDataSheetURL={safetyDataSheetURL}
            sellSheetURL={sellSheetURL}
            variantPackUUID={String(selectedItem.value)}
          />
          {relatedVariantPacks.length ? (
            <RichInfoSection
              horizontal
              style={styles.similarItemsContainer}
              title={i18n.t('similar_items')}
            >
              <ItemDetailsRecommendedItemList
                items={relatedVariantPacks}
                onSelect={(item) => {
                  onSelectItem(item, 'item_detail_related_items');
                }}
                renderActions={renderActions}
                updateQuantity={updateQuantity}
              />
            </RichInfoSection>
          ) : null}
          <ItemLegalDisclaimer />
        </View>
      </ScrollView>
      {partitionSelectorVariantPackUUID ? (
        <OrderGuidePartitionSectionActionSheet
          onClose={() => setPartitionSelectorVariantPackUUID(null)}
          variantPackUUID={partitionSelectorVariantPackUUID}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  base: {
    // keep same padding as default action sheet so scroll bar appears in correct spot
    paddingHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  brandName: {
    marginBottom: 8,
  },
  headerActions: {
    marginTop: SCREEN_PADDING,
  },
  heading: {
    flexDirection: 'row',
    marginBottom: 40,
  },
  headingSection: {
    flex: 1,
  },
  itemContainer: {
    ...BORDER.primary,
    marginBottom: 16,
  },
  sampleRequestContainer: {
    marginBottom: 8,
  },
  scrollViewContainer: {
    paddingBottom: SCREEN_PADDING,
    paddingHorizontal: 40,
  },
  similarItemsContainer: {
    marginTop: SCREEN_PADDING,
  },
});
